if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        dots: this.querySelector('.product-gallery-dots')
      }
      this.mql = window.matchMedia('(min-width: 750px)');

      this.initDots = this.initDots.bind(this);
      this.updateDots = this.updateDots.bind(this);

      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', debounce(() => this.updateDots(), 50));
      }

      this.mql.addEventListener('change', this.initDots);
      window.addEventListener('resize', this.initDots);

      this.initDots();

      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    initDots() {
      if (!this.elements.dots || !this.elements.viewer) return;

      const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
      const dotsWrapper = this.elements.dots.querySelector('.slider-counter__wrapper');
      if (!slider || !dotsWrapper) return;

      if (this.mql.matches) {
        this.elements.dots.hidden = true;
        return;
      }

      const slides = Array.from(slider.querySelectorAll('[id^="Slide-"]'));
      if (slides.length < 2) {
        this.elements.dots.hidden = true;
        return;
      }

      const itemOffset = () => (slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : slides[0]?.clientWidth || 0);
      const totalPages = slides.length;

      dotsWrapper.innerHTML = '';
      for (let i = 0; i < totalPages; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'slider-counter__link slider-counter__link--dots link';
        btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
        btn.innerHTML = '<span class="dot"></span>';
        btn.addEventListener('click', () => {
          const offset = itemOffset();
          slider.scrollTo({ left: i * offset, behavior: 'smooth' });
        });
        dotsWrapper.appendChild(btn);
      }

      if (!this._dotsScrollBound) {
        slider.addEventListener('scroll', this.updateDots, { passive: true });
        this._dotsScrollBound = true;
      }

      this.elements.dots.hidden = false;
      this.updateDots();
    }

    updateDots() {
      if (!this.elements.dots || !this.elements.viewer || this.mql.matches) return;

      const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
      const dotsWrapper = this.elements.dots.querySelector('.slider-counter__wrapper');
      if (!slider || !dotsWrapper) return;

      const slides = Array.from(slider.querySelectorAll('[id^="Slide-"]'));
      if (slides.length < 2) return;

      const offset = slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : slides[0].clientWidth;
      const page = offset ? Math.round(slider.scrollLeft / offset) + 1 : 1;

      const dots = Array.from(dotsWrapper.querySelectorAll('.slider-counter__link'));
      dots.forEach((d) => {
        d.classList.remove('slider-counter__link--active');
        d.removeAttribute('aria-current');
      });
      if (dots[page - 1]) {
        dots[page - 1].classList.add('slider-counter__link--active');
        dots[page - 1].setAttribute('aria-current', 'true');
      }
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
